import React, { useState, useEffect } from "react";

const days = [
  { full: "Sun", short: "Su" },
  { full: "Mon", short: "Mo" },
  { full: "Tue", short: "Tu" },
  { full: "Wed", short: "We" },
  { full: "Thu", short: "Th" },
  { full: "Fri", short: "Fr" },
  { full: "Sat", short: "Sa" },
];

interface CalendarProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  readOnly?: boolean;
  singleDateMode?: boolean; // New prop for single date selection
}

const Calendar: React.FC<CalendarProps> = ({
  startDate,
  endDate,
  onDateChange,
  readOnly = false,
  singleDateMode = false, // Default to range mode
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(startDate || today);

  // Update currentMonth when startDate changes
  useEffect(() => {
    if (startDate) {
      setCurrentMonth(
        new Date(startDate.getFullYear(), startDate.getMonth(), 1),
      );
    }
  }, [startDate]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDate = new Date(year, month + 1, 0).getDate();
  const startDay = firstDay.getDay();

  const dates: (Date | null)[] = [];

  for (let i = 0; i < startDay; i++) dates.push(null);
  for (let i = 1; i <= lastDate; i++) dates.push(new Date(year, month, i));

  const handlePrevMonth = () => {
    if (!readOnly) {
      setCurrentMonth(new Date(year, month - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (!readOnly) {
      setCurrentMonth(new Date(year, month + 1, 1));
    }
  };

  const handleDateClick = (date: Date) => {
    if (readOnly) return;

    if (singleDateMode) {
      // For single date mode, always set the clicked date as start and null as end
      onDateChange(date, null);
    } else {
      // Original range selection logic
      if (!startDate || (startDate && endDate)) {
        onDateChange(date, null);
      } else {
        onDateChange(startDate, date);
      }
    }
  };

  const isSameDay = (d1: Date, d2: Date) =>
    d1.toDateString() === d2.toDateString();
  const isInRange = (date: Date) =>
    startDate && endDate && date > startDate && date < endDate;

  const renderDay = (date: Date) => {
    const isStart = startDate && isSameDay(date, startDate);
    const isEnd = endDate && isSameDay(date, endDate);
    const inRange = startDate && endDate && isInRange(date);
    const isToday = isSameDay(date, today);

    const base =
      "flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors sm:h-10 sm:w-10 sm:text-base";

    if (singleDateMode) {
      // Single date mode styling
      if (isStart) {
        return (
          <div className={`${base} bg-primary text-white font-semibold`}>
            {date.getDate()}
          </div>
        );
      } else if (isToday) {
        return (
          <div
            className={`${base} border-2 border-primary text-primary hover:bg-primary/10`}
          >
            {date.getDate()}
          </div>
        );
      } else {
        return (
          <div className={`${base} hover:bg-gray-200 text-gray-700`}>
            {date.getDate()}
          </div>
        );
      }
    } else {
      // Original range mode styling
      if (isStart || isEnd) {
        if (isStart && isEnd) {
          return (
            <div
              className={`${base} relative bg-primary/80 font-semibold text-white shadow-lg`}
            >
              <div
                className={`absolute inset-0 flex items-center justify-center rounded-full border-2 border-primary`}
              ></div>
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs font-semibold text-white sm:h-8 sm:w-8 sm:text-sm`}
              >
                {date.getDate()}
              </div>
            </div>
          );
        }
        const endStyle = isEnd ? " border-2 border-primary" : "";
        return (
          <div
            className={`${base} bg-primary text-white font-semibold ${endStyle}`}
          >
            {date.getDate()}
          </div>
        );
      } else if (inRange) {
        return (
          <div className={`${base} bg-primary/20 text-black`}>
            {date.getDate()}
          </div>
        );
      } else {
        return (
          <div className={`${base} hover:bg-gray-200`}>{date.getDate()}</div>
        );
      }
    }
  };

  return (
    <div className="w-full overflow-hidden">
      <div className="mb-4 flex items-center justify-between gap-2 sm:gap-3">
        <button
          type="button"
          onClick={handlePrevMonth}
          className={`inline-flex min-w-[72px] items-center justify-center rounded-full bg-[#f3f4f6] px-3 py-2 text-xs font-semibold text-slate-500 transition hover:text-slate-700 sm:min-w-[88px] sm:px-4 sm:text-sm ${
            readOnly ? "cursor-not-allowed opacity-50" : ""
          }`}
          disabled={readOnly}
        >
          Prev
        </button>
        <div className="min-w-0 flex-1 text-center text-xl font-semibold leading-none text-slate-800">
          {currentMonth.toLocaleString("default", { month: "short" })} {year}
        </div>
        <button
          type="button"
          onClick={handleNextMonth}
          className={`inline-flex min-w-[72px] items-center justify-center rounded-full bg-[#f3f4f6] px-3 py-2 text-xs font-semibold text-slate-500 transition hover:text-slate-700 sm:min-w-[88px] sm:px-4 sm:text-sm ${
            readOnly ? "cursor-not-allowed opacity-50" : ""
          }`}
          disabled={readOnly}
        >
          Next
        </button>
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400 sm:text-[11px] sm:tracking-[0.12em]">
        {days.map((day) => (
          <div key={day.full} className="text-center">
            <span className="sm:hidden">{day.short}</span>
            <span className="hidden sm:inline">{day.full}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {dates.map((date, i) => (
          <button
            type="button"
            key={i}
            disabled={!date || readOnly}
            className={`flex min-h-[42px] items-center justify-center p-0.5 text-center sm:min-h-[48px] ${
              readOnly ? "cursor-not-allowed" : ""
            }`}
            onClick={() => date && handleDateClick(date)}
          >
            {date ? renderDay(date) : ""}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
